import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Stack,
  Typography,
  useTheme,
  Fade,
  LinearProgress,
  Button,
  Link,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';

import DashboardHeader from '@/@mui/dashboard/DashboardHeader';
import { useGetAllUnitsInPropertyLocation } from '@/api/endpoints/unit/useGetAllUnitsInPropertyLocation';
import { PMPRoutes } from '@/config/routes';
import { useAuth } from '@/context/AuthProvider';
import { useOnboardingProgress } from '@/context/OnboardingProgressProvider';
import { usePropertyManager } from '@/context/PropertyManagerProvider';
import {
  ChecklistItem,
  getChecklistItems,
  getCompletionPercentage,
  getNextMandatoryItem,
  getNextOptionalItem,
} from '@/utils/checklist';
import { getFirstName } from '@/utils/user';

import LoadingState from '@/components/LoadingState';
import TerriLottie from '@/components/lottie/TerriLottie';

import ChecklistStep from './checklist/ChecklistStep';

const ChecklistPage: React.FC = () => {
  const { onboardingProgress, isFetching, refetch } = useOnboardingProgress();
  const { user } = useAuth();
  const theme = useTheme();
  const { selectedPropertyLocation, isSingleFamilyLocation, isPropertyOwner } =
    usePropertyManager();
  const [expandedItem, setExpandedItem] = useState<ChecklistItem | null>(null);
  const [showRentalManagementSteps, setShowRentalManagementSteps] = useState(!isPropertyOwner);

  const getAllUnits = useGetAllUnitsInPropertyLocation({
    propertyLocationId: selectedPropertyLocation?.id,
  });

  useEffect(() => {
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checklistItems = useMemo(() => {
    const allItems = getChecklistItems({
      isSingleFamilyLocation,
      onboardingProgress,
      selectedPropertyLocation,
      user,
      units: getAllUnits.data ?? [],
    });

    // For property owners, filter out rental management steps if toggle is off
    if (isPropertyOwner && !showRentalManagementSteps) {
      // Keep only "Complete personal details" and "Find your property" steps
      return allItems.filter((item, index) => index < 2);
    }

    return allItems;
  }, [
    isSingleFamilyLocation,
    onboardingProgress,
    selectedPropertyLocation,
    user,
    getAllUnits.data,
    isPropertyOwner,
    showRentalManagementSteps,
  ]);

  const nextMandatoryItem = useMemo(() => getNextMandatoryItem(checklistItems), [checklistItems]);
  const nextOptionalItem = useMemo(() => getNextOptionalItem(checklistItems), [checklistItems]);
  const completionPercentage = useMemo(
    () => getCompletionPercentage(checklistItems),
    [checklistItems]
  );

  // Auto-expand the active step
  useEffect(() => {
    if (nextMandatoryItem) {
      setExpandedItem(nextMandatoryItem);
    }
  }, [nextMandatoryItem]);

  const onToggleExpand = useCallback((item: ChecklistItem) => {
    setExpandedItem(_expandedItem => (_expandedItem?.title === item.title ? null : item));
  }, []);

  return (
    <Stack direction="column" spacing={3} sx={{ flex: '1 1 100%' }}>
      <DashboardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h5">{`Welcome, ${getFirstName(user)}`}</Typography>
            <TerriLottie size={32} sx={{ mt: -0.5 }} />
          </Box>
        }
        size="small"
      />

      {isFetching || !user ? (
        <LoadingState />
      ) : (
        <Card>
          <CardHeader
            title={
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography variant="subtitle1">
                  {selectedPropertyLocation
                    ? `Setting Up ${selectedPropertyLocation.name}`
                    : 'Setting Up Your Property'}
                </Typography>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    bgcolor: 'transparent',
                    borderRadius: '12px',
                    px: 1.5,
                    py: 0.5,
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Typography variant="body2">
                    <Typography
                      variant="subtitle2"
                      color="primary"
                      component="span"
                      sx={{ fontWeight: 'bold' }}
                    >
                      {completionPercentage}%
                    </Typography>{' '}
                    Complete
                  </Typography>
                </Box>
              </Stack>
            }
            sx={{
              borderBottom: '1px solid',
              borderColor: 'divider',
              pr: 4,
              pl: 3,
            }}
          />
          <LinearProgress
            variant="determinate"
            value={completionPercentage}
            sx={{
              height: 4,
              borderRadius: 0,
              '& .MuiLinearProgress-bar': {
                transition: 'transform 1s ease-in-out',
              },
            }}
          />
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {isPropertyOwner
                  ? "You're ready to start managing your real property."
                  : 'Complete these steps to fully set up your property management system. Each step unlocks new features to help you manage your properties more effectively.'}
              </Typography>
            </Box>

            <Stack direction="column" spacing={0.5}>
              {checklistItems.map((item, index) => (
                <div key={item.title}>
                  <ChecklistStep
                    item={item}
                    index={index}
                    isExpanded={expandedItem?.title === item.title}
                    isNextMandatory={item === nextMandatoryItem}
                    isNextOptional={item === nextOptionalItem}
                    onToggleExpand={onToggleExpand}
                    isOptional={!!item.isOptional}
                  />

                  {/* Toggle button for property owners after "Find your property" step */}
                  {isPropertyOwner && false && item.title === 'Find your property' && (
                    <Box sx={{ mt: 2, mb: 2 }}>
                      <Button
                        variant="outlined"
                        onClick={() => setShowRentalManagementSteps(!showRentalManagementSteps)}
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 'medium',
                          px: 3,
                          py: 1.5,
                          borderColor: theme.palette.primary.main,
                          color: theme.palette.primary.main,
                          '&:hover': {
                            backgroundColor: `${theme.palette.primary.light}20`,
                            borderColor: theme.palette.primary.dark,
                          },
                        }}
                      >
                        {showRentalManagementSteps
                          ? 'Hide rental management steps'
                          : 'Want to manage as a rental property?'}
                      </Button>
                      {!showRentalManagementSteps && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          Click to see additional steps for managing your property as a rental.
                        </Typography>
                      )}
                    </Box>
                  )}
                </div>
              ))}
            </Stack>

            {completionPercentage >= 75 && completionPercentage < 100 && (
              <Fade in timeout={1000}>
                <Box
                  sx={{
                    mt: 3,
                    p: 2,
                    borderRadius: 2,
                    bgcolor: 'rgba(84, 214, 44, 0.12)',
                    color: 'success.dark',
                    border: `1px solid ${theme.palette.success.light}`,
                  }}
                >
                  <Typography variant="subtitle2">Almost there!</Typography>
                  <Typography variant="body2">
                    You're making great progress! Just a few more steps to fully set up your
                    property management system.
                  </Typography>
                </Box>
              </Fade>
            )}

            {completionPercentage === 100 && (
              <>
                <Fade in timeout={1000}>
                  <Box
                    sx={{
                      mt: 3,
                      p: 2,
                      borderRadius: 2,
                      bgcolor: 'rgba(84, 214, 44, 0.12)',
                      color: 'success.dark',
                      border: `1px solid ${theme.palette.success.light}`,
                      textAlign: 'center',
                    }}
                  >
                    <Typography variant="h6">Congratulations! 🎉</Typography>
                    <Typography variant="body2">
                      {isPropertyOwner
                        ? "You've completed all setup steps. Your real property management system is now ready!"
                        : "You've completed all setup steps. Your property management system is now ready!"}
                    </Typography>
                  </Box>
                </Fade>
                {isPropertyOwner && (
                  <Fade in timeout={1000}>
                    <Box
                      sx={{
                        mt: 3,
                        p: 3,
                        borderRadius: 2,
                        bgcolor: 'warning.lighter',
                        border: '2px solid',
                        borderColor: 'warning.main',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          color: 'warning.dark',
                          fontWeight: 600,
                          mb: 1,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <span role="img" aria-label="important">
                          ⚠️
                        </span>
                        Important Next Steps
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.primary' }}>
                        Start uploading your documents by going to the{' '}
                        <Link
                          href={PMPRoutes.vault}
                          sx={{
                            color: 'warning.dark',
                            fontWeight: 600,
                            textDecoration: 'underline',
                            '&:hover': { color: 'warning.main' },
                          }}
                        >
                          Document Vault
                        </Link>{' '}
                        page or forward them as email attachments to{' '}
                        <Link
                          href={`mailto:${selectedPropertyLocation?.email?.value ?? ''}`}
                          sx={{
                            color: 'warning.dark',
                            fontWeight: 600,
                            textDecoration: 'underline',
                            '&:hover': { color: 'warning.main' },
                          }}
                        >
                          {selectedPropertyLocation?.email?.value ?? ''}
                        </Link>
                        . As you start uploading documents, our AI will start to analyze them and
                        power your{' '}
                        <Link
                          href={PMPRoutes.calendar}
                          sx={{
                            color: 'warning.dark',
                            fontWeight: 600,
                            textDecoration: 'underline',
                            '&:hover': { color: 'warning.main' },
                          }}
                        >
                          Property Calendar
                        </Link>{' '}
                        and start sending you reminders. You can also interact with your documents
                        by going to the{' '}
                        <Link
                          href={PMPRoutes.chatWithAI}
                          sx={{
                            color: 'warning.dark',
                            fontWeight: 600,
                            textDecoration: 'underline',
                            '&:hover': { color: 'warning.main' },
                          }}
                        >
                          Chat with AI
                        </Link>
                        .
                      </Typography>
                    </Box>
                  </Fade>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}
    </Stack>
  );
};

export default ChecklistPage;
